import React, {useMemo} from 'react'
import {cx} from '@emotion/css'
import {SplitBookingBundle} from '@findhotel/sapi'
import {SecondaryOffer} from 'components/Offer/Secondary'
import {SplitBookingOfferInfo} from 'components/Offer/types'
import {Hotel} from 'modules/sapiSearch/slice'

import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {OfferSecondaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {SecondaryOfferListItem, SecondaryOfferListWrapper} from './styles'

type OfferListItemVariant = 'vertical' | 'verticalInline' | 'horizontal'

interface MainProps {
  isLoading?: false
  nLoaders?: never
  secondaryOffers: SearchOffer[]
  hotelId: Hotel['objectID']
  splitBookingOffers?: SplitBookingOfferInfo[]
  variant: OfferListItemVariant
  /** Allows to pass a component that will be rendered at the end of the list */
  viewMoreSlot?: React.ReactNode
  anchorPrice: number
  anchorPriceTotal: number
  sourceComponent: string
  showSplitBookingLoader?: boolean
  splitBookingPosition?: number
  splitBookingBundle?: SplitBookingBundle
  showPriceIndication?: boolean
  showCashback?: boolean
  cashbackVariant?: 'short' | 'long'
}

interface LoadingProps {
  isLoading: true
  nLoaders?: number
  variant: OfferListItemVariant
  secondaryOffers?: never
  hotelId?: never
  splitBookingOffers?: never
  viewMoreSlot?: never
  anchorPrice?: never
  anchorPriceTotal?: never
  sourceComponent?: never
  showSplitBookingLoader?: never
  splitBookingPosition?: number
  splitBookingBundle?: never
  showPriceIndication?: never
  showCashback?: never
  cashbackVariant?: never
}

const offerLayoutVariantMap: Record<
  OfferListItemVariant,
  OfferSecondaryLayoutVariant
> = {
  horizontal: 'SecondaryWithoutRoomName',
  vertical: 'SecondaryDefault',
  verticalInline: 'SecondaryInline'
}

const SecondaryOfferListLoader = ({
  variant,
  nLoaders
}: {
  variant: OfferListItemVariant
  nLoaders?: number
}) => {
  const dividerSideMargin = variant === 'vertical' ? 's400' : 's500'

  return (
    <SecondaryOfferListWrapper
      className={cx(variant === 'horizontal' && '--horizontal')}
      isLoading
    >
      {Array(nLoaders)
        .fill(0)
        .map((_, index) => (
          <React.Fragment key={`loading_${index}`}>
            {index > 0 && variant !== 'horizontal' && (
              <Divider sideMargin={dividerSideMargin} />
            )}
            <SecondaryOfferListItem
              className={cx(
                variant === 'horizontal' && '--horizontal',
                variant === 'verticalInline' && '--vertical-inline'
              )}
            >
              <SecondaryOffer
                variant={offerLayoutVariantMap[variant]}
                isLoading
              />
            </SecondaryOfferListItem>
          </React.Fragment>
        ))}
      {variant === 'horizontal' && (
        <Box
          paddingTop="s300"
          paddingBottom="s300"
          paddingLeft="s400"
          paddingRight="s400"
        >
          <SkeletonLoader width={62} />
          <SkeletonLoader width={48} />
        </Box>
      )}
    </SecondaryOfferListWrapper>
  )
}

export const SecondaryOfferList: React.FC<MainProps | LoadingProps> = ({
  secondaryOffers,
  isLoading,
  nLoaders = 2,
  hotelId,
  variant = 'vertical',
  viewMoreSlot,
  splitBookingOffers,
  showSplitBookingLoader,
  anchorPrice,
  anchorPriceTotal,
  sourceComponent,
  splitBookingPosition = 2,
  splitBookingBundle,
  showPriceIndication = false,
  showCashback,
  cashbackVariant = 'long'
}) => {
  const dividerSideMargin =
    variant === 'vertical' ? 's400' : isLoading ? 's500' : 's000'

  const secondarySplitBookingOffer = useMemo(() => {
    const showDividerAfter =
      variant !== 'horizontal' &&
      splitBookingPosition === 1 &&
      secondaryOffers?.length > 0

    const showDividerBefore =
      variant !== 'horizontal' && splitBookingPosition >= 2

    if (showSplitBookingLoader)
      return (
        <SecondaryOfferListItem
          key={'SplitBookingOffer'}
          className={cx(
            variant === 'horizontal' && '--horizontal',
            variant === 'verticalInline' && '--vertical-inline',
            '--is-split-booking-loading'
          )}
        >
          {showDividerBefore && <Divider sideMargin={dividerSideMargin} />}
          <SecondaryOffer isLoading variant={offerLayoutVariantMap[variant]} />
          {showDividerAfter && <Divider sideMargin={dividerSideMargin} />}
        </SecondaryOfferListItem>
      )

    return (
      splitBookingOffers?.length > 0 && (
        <SecondaryOfferListItem
          data-id="SecondarySplitBookingOffer"
          key={'SplitBookingOffer'}
          className={cx(
            variant === 'horizontal' && '--horizontal',
            variant === 'verticalInline' && '--vertical-inline'
          )}
        >
          {showDividerBefore && <Divider sideMargin={dividerSideMargin} />}
          <SecondaryOffer
            isSplitBooking
            hotelId={hotelId}
            sourceComponent={sourceComponent}
            variant={offerLayoutVariantMap[variant]}
            splitBookingOffers={splitBookingOffers}
            splitBookingBundle={splitBookingBundle}
          />
          {showDividerAfter && <Divider sideMargin={dividerSideMargin} />}
        </SecondaryOfferListItem>
      )
    )
  }, [
    variant,
    splitBookingPosition,
    showSplitBookingLoader,
    splitBookingOffers,
    hotelId,
    sourceComponent,
    splitBookingBundle,
    dividerSideMargin,
    secondaryOffers
  ])

  if (isLoading)
    return <SecondaryOfferListLoader variant={variant} nLoaders={nLoaders} />

  return (
    <SecondaryOfferListWrapper
      className={cx(variant === 'horizontal' && '--horizontal')}
      data-id="SecondaryOfferList"
    >
      {splitBookingPosition == 1 && secondarySplitBookingOffer}
      {secondaryOffers.map((offer, index) => (
        <SecondaryOfferListItem
          key={offer.id}
          className={cx(
            variant === 'horizontal' && '--horizontal',
            variant === 'verticalInline' && '--vertical-inline'
          )}
        >
          {variant !== 'horizontal' && index > 0 && (
            <Divider sideMargin={dividerSideMargin} />
          )}
          <SecondaryOffer
            anchorPrice={anchorPrice}
            anchorPriceTotal={anchorPriceTotal}
            variant={offerLayoutVariantMap[variant]}
            offer={offer}
            hotelId={hotelId}
            hasInteraction
            sourceComponent={sourceComponent}
            offerPosition={index + 1} // secondary offers should start from 1 because primary offer is 0
            showPriceIndication={showPriceIndication}
            showCashback={showCashback}
            cashbackVariant={cashbackVariant}
          />
        </SecondaryOfferListItem>
      ))}
      {splitBookingPosition >= 2 && secondarySplitBookingOffer}
      {viewMoreSlot && (
        <SecondaryOfferListItem
          className={cx(
            variant === 'horizontal' && '--horizontal',
            variant === 'verticalInline' && '--vertical-inline'
          )}
        >
          {variant !== 'horizontal' && (
            <Divider sideMargin={dividerSideMargin} />
          )}
          {viewMoreSlot}
        </SecondaryOfferListItem>
      )}
    </SecondaryOfferListWrapper>
  )
}
